import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  BookmarkIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@/components/icons';

import type { 
  FilterSidebarProps, 
  FilterGroupConfig, 
  AdvancedFilters,
  ActiveFilter,
  FilterPreset
} from '@/types/filters';
import { UserRole } from '@/types/api';
import { FilterGroup } from './FilterGroup';
import { SavedViews } from './SavedViews';
import { FilterChips } from './FilterChips';
import { FilterSearch } from './FilterSearch';
import { DEFAULT_FILTER_PRESETS } from '@/types/filters';

/**
 * Advanced Filter Sidebar Component
 * 
 * Provides comprehensive filtering capabilities following Freshdesk patterns
 * with Webcoda design system integration.
 * 
 * Features:
 * - Collapsible sidebar with smooth animations
 * - Grouped filter categories
 * - Saved views system
 * - Real-time filter chips
 * - Responsive design with mobile overlay
 * - Keyboard navigation support
 */
export function FilterSidebar({
  filters,
  onFiltersChange,
  expanded,
  onToggle,
  savedViews,
  currentViewId,
  onSaveView,
  onLoadView,
  onDeleteView,
  onSetDefaultView,
  filterOptions,
  onLoadFilterOptions,
  className,
  width = 300,
  position = 'right',
  overlay = false,
  enabledGroups,
  groupConfig,
  userRole,
  userId,
  onSearchSuggestions,
  onValidateFilters,
  onFilterAnalytics
}: FilterSidebarProps) {
  
  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [showPresets, setShowPresets] = useState(false);
  const [loading, setLoading] = useState(false);

  // Default filter groups configuration
  const defaultGroupConfig: FilterGroupConfig[] = [
    {
      id: 'search',
      title: 'Search & Quick Filters',
      icon: <MagnifyingGlassIcon className="w-4 h-4" />,
      defaultExpanded: true,
      collapsible: false,
      order: 1,
      filters: [
        {
          id: 'search',
          type: 'search',
          title: 'Search',
          placeholder: 'Search ticket subjects and descriptions...',
          order: 1
        }
      ]
    },
    {
      id: 'assignment',
      title: 'Agent & Assignment',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>,
      defaultExpanded: true,
      collapsible: true,
      order: 2,
      visible: userRole !== UserRole.EndUser,
      filters: [
        {
          id: 'assignedAgents',
          type: 'multiselect',
          title: 'Assigned Agent',
          multiple: true,
          searchable: true,
          order: 1
        },
        {
          id: 'unassignedOnly',
          type: 'toggle',
          title: 'Unassigned Only',
          order: 3
        },
        {
          id: 'myTicketsOnly',
          type: 'toggle',
          title: 'My Tickets',
          order: 4
        }
      ]
    },
    {
      id: 'properties',
      title: 'Ticket Properties',
      icon: <AdjustmentsHorizontalIcon className="w-4 h-4" />,
      defaultExpanded: true,
      collapsible: true,
      order: 3,
      filters: [
        {
          id: 'statuses',
          type: 'multiselect',
          title: 'Status',
          multiple: true,
          order: 1
        },
        {
          id: 'priorities',
          type: 'multiselect',
          title: 'Priority', 
          multiple: true,
          order: 2
        },
        {
          id: 'sources',
          type: 'multiselect',
          title: 'Source',
          multiple: true,
          order: 3
        }
      ]
    },
    {
      id: 'customer',
      title: 'Customer Information',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>,
      defaultExpanded: false,
      collapsible: true,
      order: 4,
      filters: [
        {
          id: 'companies',
          type: 'companySelect',
          title: 'Company',
          multiple: true,
          searchable: true,
          order: 2
        }
      ]
    },
    {
      id: 'dates',
      title: 'Date Ranges',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>,
      defaultExpanded: false,
      collapsible: true,
      order: 5,
      filters: [
        {
          id: 'createdDateRange',
          type: 'dateRange',
          title: 'Created Date',
          order: 1
        },
        {
          id: 'updatedDateRange',
          type: 'dateRange',
          title: 'Updated Date',
          order: 2
        }
      ]
    },
    {
      id: 'advanced',
      title: 'Advanced Options',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
      </svg>,
      defaultExpanded: false,
      collapsible: true,
      order: 6,
      filters: [
        {
          id: 'hasAttachments',
          type: 'checkbox',
          title: 'Has Attachments',
          order: 2
        },
        {
          id: 'escalated',
          type: 'checkbox',
          title: 'Escalated',
          order: 5
        }
      ]
    }
  ];

  // Filter available presets based on user role
  const availablePresets = useMemo(() => {
    return DEFAULT_FILTER_PRESETS.filter(preset => 
      preset.userRoles.includes(userRole)
    ).sort((a, b) => a.order - b.order);
  }, [userRole]);

  // Merge default config with custom config
  const finalGroupConfig = useMemo(() => {
    const merged = defaultGroupConfig.map(group => ({
      ...group,
      ...groupConfig?.[group.id]
    }));

    // Filter by enabled groups if specified
    if (enabledGroups) {
      return merged.filter(group => enabledGroups.includes(group.id));
    }

    // Filter by visibility
    return merged.filter(group => group.visible !== false);
  }, [defaultGroupConfig, groupConfig, enabledGroups]);

  // Initialize expanded groups based on defaultExpanded property
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initialExpanded: Record<string, boolean> = {};
    finalGroupConfig.forEach(group => {
      initialExpanded[group.id] = group.defaultExpanded || false;
    });
    return initialExpanded;
  });

  // Generate active filters for chips
  const generateActiveFilters = useCallback((currentFilters: AdvancedFilters): ActiveFilter[] => {
    const active: ActiveFilter[] = [];

    // Search filter
    if (currentFilters.search) {
      active.push({
        id: 'search',
        groupId: 'search',
        filterId: 'search',
        label: 'Search',
        value: currentFilters.search,
        displayValue: `"${currentFilters.search}"`,
        removable: true
      });
    }

    // Status filters
    currentFilters.statuses?.forEach(status => {
      active.push({
        id: `status-${status}`,
        groupId: 'properties',
        filterId: 'statuses',
        label: 'Status',
        value: status,
        displayValue: status,
        removable: true,
        color: getStatusColor(status)
      });
    });

    // Priority filters
    currentFilters.priorities?.forEach(priority => {
      active.push({
        id: `priority-${priority}`,
        groupId: 'properties', 
        filterId: 'priorities',
        label: 'Priority',
        value: priority,
        displayValue: priority,
        removable: true,
        color: getPriorityColor(priority)
      });
    });

    currentFilters.sources.forEach(source => active.push({
      id: `source-${source}`, groupId: 'properties', filterId: 'sources',
      label: 'Source', value: source, displayValue: source, removable: true
    }));
    currentFilters.assignedAgents.forEach(agentId => {
      const agent = filterOptions.agents.find(a => a.userId === agentId);
      active.push({
        id: `agent-${agentId}`, groupId: 'assignment', filterId: 'assignedAgents',
        label: 'Agent', value: agentId, displayValue: agent?.userName || agentId, removable: true
      });
    });
    currentFilters.companies.forEach(companyId => {
      const company = filterOptions.companies.find(c => c.companyId === companyId);
      active.push({
        id: `company-${companyId}`, groupId: 'customer', filterId: 'companies',
        label: 'Company', value: companyId, displayValue: company?.companyName || companyId, removable: true
      });
    });

    // Assignment filters
    if (currentFilters.unassignedOnly) {
      active.push({
        id: 'unassigned',
        groupId: 'assignment',
        filterId: 'unassignedOnly',
        label: 'Assignment',
        value: true,
        displayValue: 'Unassigned',
        removable: true
      });
    }

    if (currentFilters.myTicketsOnly) {
      active.push({
        id: 'mytickets',
        groupId: 'assignment',
        filterId: 'myTicketsOnly',
        label: 'Assignment',
        value: true,
        displayValue: 'My Tickets',
        removable: true
      });
    }

    // Date ranges
    if (currentFilters.createdDateRange) {
      active.push({
        id: 'created-date',
        groupId: 'dates',
        filterId: 'createdDateRange',
        label: 'Created',
        value: currentFilters.createdDateRange,
        displayValue: formatDateRange(currentFilters.createdDateRange),
        removable: true
      });
    }
    if (currentFilters.updatedDateRange) {
      active.push({
        id: 'updated-date', groupId: 'dates', filterId: 'updatedDateRange',
        label: 'Updated', value: currentFilters.updatedDateRange,
        displayValue: formatDateRange(currentFilters.updatedDateRange), removable: true
      });
    }
    if (currentFilters.hasAttachments === true) {
      active.push({
        id: 'has-attachments', groupId: 'advanced', filterId: 'hasAttachments',
        label: 'Attachments', value: true, displayValue: 'Has Attachments', removable: true
      });
    }
    if (currentFilters.escalated === true) {
      active.push({
        id: 'escalated', groupId: 'advanced', filterId: 'escalated',
        label: 'Escalated', value: true, displayValue: 'Escalated', removable: true
      });
    }

    // Tags
    currentFilters.tags?.forEach(tag => {
      active.push({
        id: `tag-${tag}`,
        groupId: 'advanced',
        filterId: 'tags',
        label: 'Tag',
        value: tag,
        displayValue: tag,
        removable: true
      });
    });

    return active;
  }, [filterOptions.agents, filterOptions.companies]);

  // Update active filters when filters change
  useEffect(() => {
    const active = generateActiveFilters(filters);
    setActiveFilters(active);
  }, [filters, generateActiveFilters]);

  // Initialize expanded groups
  useEffect(() => {
    const initialExpanded: Record<string, boolean> = {};
    finalGroupConfig.forEach(group => {
      initialExpanded[group.id] = group.defaultExpanded ?? false;
    });
    setExpandedGroups(prev => {
      // Only update if the group doesn't already have a state set
      const newExpanded = { ...prev };
      let hasChanges = false;

      finalGroupConfig.forEach(group => {
        if (!(group.id in prev)) {
          newExpanded[group.id] = group.defaultExpanded ?? false;
          hasChanges = true;
        }
      });

      return hasChanges ? newExpanded : prev;
    });
  }, [finalGroupConfig]);

  // Load filter options on mount
  useEffect(() => {
    onLoadFilterOptions();
  }, [onLoadFilterOptions]);

  // Handle filter change
  const handleFilterChange = useCallback((newFilters: AdvancedFilters) => {
    onFiltersChange(newFilters);
    
    // Analytics
    onFilterAnalytics?.('filter_changed', {
      userId,
      filters: newFilters,
      timestamp: Date.now()
    });
  }, [onFiltersChange, onFilterAnalytics, userId]);

  // Handle group toggle
  const handleGroupToggle = useCallback((groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  }, []);

  // Handle search within filters
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Handle filter removal
  const handleRemoveFilter = useCallback((filterId: string) => {
    const filter = activeFilters.find(f => f.id === filterId);
    if (!filter) return;

    const newFilters = { ...filters };
    
    // Handle different filter types
    switch (filter.filterId) {
      case 'search':
        newFilters.search = '';
        break;
      case 'statuses':
        newFilters.statuses = newFilters.statuses?.filter(s => s !== filter.value) || [];
        break;
      case 'priorities':
        newFilters.priorities = newFilters.priorities?.filter(p => p !== filter.value) || [];
        break;
      case 'sources':
        newFilters.sources = newFilters.sources.filter(source => source !== filter.value);
        break;
      case 'assignedAgents':
        newFilters.assignedAgents = newFilters.assignedAgents.filter(id => id !== filter.value);
        break;
      case 'companies':
        newFilters.companies = newFilters.companies.filter(id => id !== filter.value);
        break;
      case 'unassignedOnly':
        newFilters.unassignedOnly = false;
        break;
      case 'myTicketsOnly':
        newFilters.myTicketsOnly = false;
        break;
      case 'tags':
        newFilters.tags = newFilters.tags?.filter(t => t !== filter.value) || [];
        break;
      case 'createdDateRange':
        newFilters.createdDateRange = null;
        break;
      case 'updatedDateRange':
        newFilters.updatedDateRange = null;
        break;
      case 'hasAttachments':
        newFilters.hasAttachments = null;
        break;
      case 'escalated':
        newFilters.escalated = null;
        break;
      // Add more cases as needed
    }

    handleFilterChange(newFilters);
  }, [activeFilters, filters, handleFilterChange]);

  // Handle clear all filters
  const handleClearAll = useCallback(() => {
    const clearedFilters: AdvancedFilters = {
      search: '',
      assignedAgents: [],
      assignedGroups: [],
      unassignedOnly: false,
      myTicketsOnly: false,
      statuses: [],
      priorities: [],
      sources: [],
      types: [],
      customers: [],
      companies: [],
      vipCustomersOnly: false,
      customerSatisfactionRange: null,
      createdDateRange: null,
      updatedDateRange: null,
      dueDateRange: null,
      closedDateRange: null,
      customFields: [],
      tags: [],
      excludeTags: [],
      hasAttachments: null,
      hasConversations: null,
      isOverdue: null,
      escalated: null
    };

    handleFilterChange(clearedFilters);
  }, [handleFilterChange]);

  // Handle preset application
  const handleApplyPreset = useCallback((preset: FilterPreset) => {
    const newFilters = { ...filters, ...preset.filters };
    handleFilterChange(newFilters);
    setShowPresets(false);

    onFilterAnalytics?.('preset_applied', {
      userId,
      presetId: preset.id,
      presetName: preset.name,
      timestamp: Date.now()
    });
  }, [filters, handleFilterChange, onFilterAnalytics, userId]);

  // Sidebar styles
  const sidebarStyles = {
    width: expanded ? width : 0,
    transition: 'width 0.3s ease-in-out'
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      New: '#6B7280',
      Open: '#10B981',
      Pending: '#3B82F6', 
      OnHold: '#F59E0B',
      Resolved: '#8B5CF6',
      Closed: '#6B7280',
      Cancelled: '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      Low: '#10B981',
      Medium: '#F59E0B',
      High: '#EF4444',
      Urgent: '#DC2626',
      Critical: '#7C2D12'
    };
    return colors[priority] || '#6B7280';
  };

  const formatDateRange = (range: any) => {
    if (range?.preset) {
      const presetLabels: Record<string, string> = {
        today: 'Today',
        yesterday: 'Yesterday',
        thisWeek: 'This Week',
        lastWeek: 'Last Week',
        thisMonth: 'This Month',
        lastMonth: 'Last Month',
        last7Days: 'Last 7 Days',
        last30Days: 'Last 30 Days',
        last90Days: 'Last 90 Days'
      };
      return presetLabels[range.preset] || 'Custom Range';
    }
    return 'Custom Range';
  };

  return (
    <>
      {/* Mobile Overlay */}
      {overlay && expanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Toggle Button - only show when not in overlay mode (host handles toggle) */}
      {!overlay && (
        <button
          onClick={onToggle}
          className={clsx(
            'fixed z-50 flex items-center justify-center w-10 h-10',
            'bg-white border border-gray-200 rounded-md shadow-sm',
            'hover:bg-gray-50 hover:border-gray-300',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'transition-all duration-200',
            position === 'right' ? 'right-4' : 'left-4',
            'top-1/2 -translate-y-1/2',
          )}
          aria-label={expanded ? 'Close filters' : 'Open filters'}
        >
          <FunnelIcon className="w-5 h-5 text-gray-600" />
          {activeFilters.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
              {activeFilters.length}
            </span>
          )}
        </button>
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed top-0 h-full bg-white border-l border-gray-200 shadow-lg z-50',
          'flex flex-col overflow-hidden',
          position === 'right' ? 'right-0' : 'left-0',
          // Always use fixed positioning to avoid layout space issues
          expanded ? 'translate-x-0' : 'translate-x-full',
          className
        )}
        style={{
          width: width,
          // Ensure complete hiding when collapsed
          transform: expanded ? 'translateX(0)' : `translateX(${width + 50}px)`
        }}
        role="complementary"
        aria-label="Ticket filters"
      >
        {/* Header */}
        <div className="flex-none px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            <div className="flex items-center space-x-2">
              {/* Presets Button */}
              {availablePresets.length > 0 && (
                <button
                  onClick={() => setShowPresets(!showPresets)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-white"
                  title="Filter Presets"
                >
                  <BookmarkIcon className="w-4 h-4" />
                </button>
              )}
              
              {/* Reset Button */}
              {activeFilters.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-white"
                  title="Clear All Filters"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                </button>
              )}
              
              {/* Close Button */}
              <button
                onClick={onToggle}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-white"
                aria-label="Close filters"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Active Filters Count */}
          {activeFilters.length > 0 && (
            <p className="mt-1 text-sm text-gray-500">
              {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} applied
            </p>
          )}
        </div>

        {/* Presets Panel */}
        {showPresets && (
          <div className="flex-none p-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Filters</h3>
            <div className="space-y-2">
              {availablePresets.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => handleApplyPreset(preset)}
                  className="w-full text-left p-3 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center">
                    {preset.icon && (
                      <div className="mr-3 text-gray-400">
                        {preset.icon}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {preset.name}
                      </div>
                      {preset.description && (
                        <div className="text-xs text-gray-500">
                          {preset.description}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filter Search */}
        <div className="flex-none p-4 border-b border-gray-200">
          <FilterSearch
            value={searchQuery}
            onChange={handleSearchChange}
            onClear={() => setSearchQuery('')}
            placeholder="Search filters..."
          />
        </div>

        {/* Active Filters Chips */}
        {activeFilters.length > 0 && (
          <div className="flex-none p-4 border-b border-gray-200">
            <FilterChips
              activeFilters={activeFilters}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAll}
              maxVisible={3}
              showClearAll={true}
            />
          </div>
        )}

        {/* Saved Views */}
        <div className="flex-none border-b border-gray-200">
          <SavedViews
            views={savedViews}
            currentViewId={currentViewId}
            currentFilters={filters}
            onSave={onSaveView}
            onLoad={onLoadView}
            onDelete={onDeleteView}
            onSetDefault={onSetDefaultView}
            loading={loading}
            userRole={userRole}
            showCreateButton={true}
            showPublicViews={true}
          />
        </div>

        {/* Filter Groups */}
        <div className="flex-1 overflow-y-auto">
          {finalGroupConfig.map(groupConfig => (
            <FilterGroup
              key={groupConfig.id}
              config={groupConfig}
              filters={filters}
              onFiltersChange={handleFilterChange}
              expanded={expandedGroups[groupConfig.id] || false}
              onToggle={() => handleGroupToggle(groupConfig.id)}
              filterOptions={filterOptions}
              userRole={userRole}
              onSearchSuggestions={onSearchSuggestions}
            />
          ))}
        </div>
      </div>
    </>
  );
}
